import { hasSupabaseConfig, supabase } from '../../../shared/api/supabaseClient.js';

const SETTLEMENT_ERRORS = {
  authentication_required: '로그인 세션을 확인할 수 없습니다.',
  active_union_membership_required: '활성 유니온 가입 정보가 필요합니다.',
  union_manager_required: '주딱·파딱만 결산 코멘트를 수정할 수 있습니다.',
  invalid_season_no: '결산 차수 번호가 올바르지 않습니다.',
  settlement_comment_too_long: '결산 코멘트는 1,000자 이하로 입력해주세요.',
  raid_season_not_found: '해당 결산 차수를 찾을 수 없습니다.',
};

function requireClient() {
  if (!hasSupabaseConfig || !supabase) throw new Error('서비스 연결 설정을 확인할 수 없습니다.');
  return supabase;
}

function settlementError(error, fallback) {
  const raw = String(error?.message || '');
  const code = Object.keys(SETTLEMENT_ERRORS).find((item) => raw.includes(item));
  if (code) return new Error(SETTLEMENT_ERRORS[code]);
  if (String(error?.code || '') === '42883' || raw.includes('save_union_raid_settlement_comment')) {
    const next = new Error('결산 코멘트 저장 마이그레이션이 아직 적용되지 않았습니다.');
    next.code = 'settlement_comment_migration_required';
    return next;
  }
  return new Error(raw || fallback);
}

function correctionStatsError(error) {
  const raw = String(error?.message || '');
  const code = Object.keys(SETTLEMENT_ERRORS).find((item) => raw.includes(item));
  if (code) return new Error(SETTLEMENT_ERRORS[code]);
  if (String(error?.code || '') === '42883' || raw.includes('get_raid_settlement_correction_stats')) {
    const next = new Error('전체 유니온 레벨 구간 보정 마이그레이션이 아직 적용되지 않았습니다.');
    next.code = 'settlement_correction_migration_required';
    return next;
  }
  return new Error(raw || '전체 유니온 보정 통계를 불러오지 못했습니다.');
}

export async function loadRaidSettlementHistory(unionId) {
  const normalizedUnionId = String(unionId || '').trim();
  if (!normalizedUnionId) return { seasons: [], records: [] };

  const client = requireClient();
  const { data: seasons, error: seasonError } = await client
    .from('raid_seasons')
    .select('id,season_no,status')
    .eq('union_id', normalizedUnionId)
    .order('season_no', { ascending: true });
  if (seasonError) throw seasonError;

  const seasonRows = Array.isArray(seasons) ? seasons : [];
  const seasonIds = seasonRows.map((season) => season.id).filter(Boolean);
  if (!seasonIds.length) return { seasons: seasonRows, records: [] };

  const { data: records, error: recordError } = await client
    .from('raid_records')
    .select('season_id,member_openid,member_nickname,char_level,deck,attacked_at,updated_at')
    .eq('union_id', normalizedUnionId)
    .in('season_id', seasonIds)
    .limit(10000);
  if (recordError) throw recordError;

  return {
    seasons: seasonRows,
    records: Array.isArray(records) ? records : [],
  };
}

export async function loadRaidSettlementCorrectionStats(seasonNo) {
  const normalizedSeasonNo = Number(seasonNo);
  if (!Number.isInteger(normalizedSeasonNo) || normalizedSeasonNo < 1) {
    throw new Error(SETTLEMENT_ERRORS.invalid_season_no);
  }

  const client = requireClient();
  const { data, error } = await client.rpc('get_raid_settlement_correction_stats', {
    target_season_no: normalizedSeasonNo,
  });
  if (error) throw correctionStatsError(error);
  return data && typeof data === 'object' ? data : null;
}

export async function saveRaidSettlementComment({ seasonNo, comment }) {
  const normalizedSeasonNo = Number(seasonNo);
  if (!Number.isInteger(normalizedSeasonNo) || normalizedSeasonNo < 1) {
    throw new Error(SETTLEMENT_ERRORS.invalid_season_no);
  }
  const normalizedComment = String(comment || '').trim();
  if (normalizedComment.length > 1000) throw new Error(SETTLEMENT_ERRORS.settlement_comment_too_long);

  const client = requireClient();
  const { data, error } = await client.rpc('save_union_raid_settlement_comment', {
    target_season_no: normalizedSeasonNo,
    comment_value: normalizedComment,
  });
  if (error) throw settlementError(error, '결산 코멘트를 저장하지 못했습니다.');
  return data;
}
